import React, { useState, useEffect } from 'react';
import { StarIcon, SparklesIcon, HeartIcon } from './Icons';
import { sounds } from '../utils/audio';

// Bubble items with colors and real animal images
const initialBubbles = [
  { id: 1, label: 'Cow', sound: 'Moo moo!', img: '/assets/images/animal-cow.png', color: 'bg-rose-100 border-rose-300 text-rose-700', popped: false },
  { id: 2, label: 'Lion', sound: 'Roar!', img: '/assets/images/animal-lion.png', color: 'bg-amber-100 border-amber-300 text-amber-700', popped: false },
  { id: 3, label: 'Duck', sound: 'Quack quack!', img: '/assets/images/animal-duck.png', color: 'bg-yellow-100 border-yellow-300 text-yellow-700', popped: false },
  { id: 4, label: 'Rabbit', sound: 'Hop hop!', img: '/assets/images/animal-rabbit.png', color: 'bg-pink-100 border-pink-300 text-pink-700', popped: false },
  { id: 5, label: 'Frog', sound: 'Ribbit ribbit!', img: '/assets/images/animal-frog.png', color: 'bg-emerald-100 border-emerald-300 text-emerald-700', popped: false },
  { id: 6, label: 'Bee', sound: 'Buzzzz!', img: '/assets/images/animal-bee.png', color: 'bg-amber-100 border-amber-300 text-amber-700', popped: false },
  { id: 7, label: 'Pip', sound: 'Tweet tweet!', img: '/assets/images/pet-pip.png', color: 'bg-sky-100 border-sky-300 text-sky-700', popped: false },
  { id: 8, label: 'Dino Buddy', sound: 'Stomp stomp!', img: '/assets/images/buddy-dino.png', color: 'bg-teal-100 border-teal-300 text-teal-700', popped: false },
];

// Math questions
const mathQuestions = [
  {
    id: 1,
    island: 'Counting Cove',
    prompt: 'How many glowing fireflies do you see?',
    count: 5,
    icon: '✨',
    options: [3, 5, 7],
    answer: 5,
    hint: 'Tap and count each bright sparkle!'
  },
  {
    id: 2,
    island: 'Shape Shore',
    prompt: 'Which shape has 3 straight sides?',
    options: ['Square', 'Triangle', 'Circle'],
    answer: 'Triangle',
    hint: 'Triangles have three corners!'
  },
  {
    id: 3,
    island: 'Symbol Bridge',
    prompt: 'Drop in planks: 3 + 2 = ?',
    count1: 3,
    count2: 2,
    icon: '🪵',
    options: [4, 5, 6],
    answer: 5,
    hint: 'Put 3 and 2 planks together!'
  },
  {
    id: 4,
    island: 'Number Line',
    prompt: 'What number is hiding in the mystery box: 1, 2, [ ? ], 4',
    options: [2, 3, 5],
    answer: 3,
    hint: '1, 2, ... what comes right before 4?'
  }
];

// Soundboard items
const soundboardCharacters = [
  { name: 'Cow', soundWord: 'Moo moo!', art: '/assets/images/animal-cow.png', color: '#FFF0F2' },
  { name: 'Lion', soundWord: 'Roaaar!', art: '/assets/images/animal-lion.png', color: '#FFF8E6' },
  { name: 'Duck', soundWord: 'Quack quack!', art: '/assets/images/animal-duck.png', color: '#FEF9C3' },
  { name: 'Frog', soundWord: 'Ribbit ribbit!', art: '/assets/images/animal-frog.png', color: '#ECFDF5' },
  { name: 'Rabbit', soundWord: 'Hop hop!', art: '/assets/images/animal-rabbit.png', color: '#FDF2F8' },
  { name: 'Bee', soundWord: 'Bzzzz!', art: '/assets/images/animal-bee.png', color: '#FEF3C7' },
  { name: 'Buddy Fox', soundWord: 'Yip yip!', art: '/assets/images/buddy-fox.png', color: '#FFF7ED' },
  { name: 'Buddy Dino', soundWord: 'Rawr!', art: '/assets/images/buddy-dino.png', color: '#F0FDFA' },
  { name: 'Buddy Bunny', soundWord: 'Boing boing!', art: '/assets/images/buddy-bunny.png', color: '#FAF5FF' },
  { name: 'Buddy Bear', soundWord: 'Hello friend!', art: '/assets/images/buddy-bear.png', color: '#FEF2F2' },
  { name: 'Pet Pip', soundWord: 'Tweet tweet!', art: '/assets/images/pet-pip.png', color: '#F0F9FF' },
  { name: 'Pet Snuffle', soundWord: 'Snuff snuff!', art: '/assets/images/pet-snuffle.png', color: '#F5F3FF' },
];

export default function Playground() {
  const [activeTab, setActiveTab] = useState('bubbles'); // 'bubbles' | 'math' | 'soundboard'

  // Bubble Game State
  const [bubbles, setBubbles] = useState(initialBubbles);
  const [popScore, setPopScore] = useState(0);
  const [lastPopped, setLastPopped] = useState(null);

  // Math Game State
  const [currentMathIdx, setCurrentMathIdx] = useState(0);
  const [mathStars, setMathStars] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [mathFeedback, setMathFeedback] = useState(null);
  const [showChestReward, setShowChestReward] = useState(false);

  // Soundboard state
  const [activeSpeaker, setActiveSpeaker] = useState(null);

  const handlePop = (id, item) => {
    sounds.playSound('pop');
    setBubbles(prev => prev.map(b => b.id === id ? { ...b, popped: true } : b));
    setPopScore(s => s + 1);
    setLastPopped(item);
  };

  const resetBubbles = () => {
    sounds.playSound('tap');
    setBubbles(initialBubbles.map(b => ({ ...b, popped: false })));
  };

  const handleMathChoice = (choice) => {
    setSelectedOption(choice);
    const q = mathQuestions[currentMathIdx];
    if (choice === q.answer) {
      sounds.playSound('chest');
      setMathFeedback({ correct: true, text: 'Super job! That is right! ⭐' });
      setMathStars(s => s + 1);
      setTimeout(() => {
        if (currentMathIdx < mathQuestions.length - 1) {
          setCurrentMathIdx(i => i + 1);
          setSelectedOption(null);
          setMathFeedback(null);
        } else {
          setShowChestReward(true);
        }
      }, 1600);
    } else {
      sounds.playSound('tap');
      setMathFeedback({ correct: false, text: `Miss Kiki hint: ${q.hint}` });
    }
  };

  const resetMath = () => {
    sounds.playSound('tap');
    setCurrentMathIdx(0);
    setSelectedOption(null);
    setMathFeedback(null);
    setShowChestReward(false);
  };

  const handleSoundboardTap = (char) => {
    sounds.playSound('feed');
    setActiveSpeaker(char.name);
    setTimeout(() => {
      setActiveSpeaker(null);
    }, 1500);
  };

  return (
    <section id="playground" className="py-16 lg:py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-kiki-yellow/30 text-amber-900 font-heading font-bold text-xs uppercase tracking-wider">
            <SparklesIcon className="w-4 h-4 text-amber-600" />
            <span>Interactive Web Playground</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black text-slate-900 tracking-tight">
            Play &amp; Learn Right Now in Your Browser!
          </h2>
          <p className="text-slate-600 font-medium text-base sm:text-lg">
            No downloads needed to start the fun. Try our joyful bubble pop, test your maths skills on Miss Kiki's islands, or explore the animal soundboard!
          </p>

          {/* Tab Selector */}
          <div className="inline-flex p-1.5 rounded-3xl bg-slate-100 border border-slate-200 mt-4 shadow-inner">
            <button
              onClick={() => { sounds.playSound('tap'); setActiveTab('bubbles'); }}
              className={`px-5 py-2.5 rounded-2xl font-heading text-sm font-bold transition-all ${
                activeTab === 'bubbles'
                  ? 'bg-kiki-coral text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              🎈 Bubble Pop!
            </button>
            <button
              onClick={() => { sounds.playSound('tap'); setActiveTab('math'); }}
              className={`px-5 py-2.5 rounded-2xl font-heading text-sm font-bold transition-all ${
                activeTab === 'math'
                  ? 'bg-kiki-blue text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              🏝️ Math Island
            </button>
            <button
              onClick={() => { sounds.playSound('tap'); setActiveTab('soundboard'); }}
              className={`px-5 py-2.5 rounded-2xl font-heading text-sm font-bold transition-all ${
                activeTab === 'soundboard'
                  ? 'bg-kiki-purple text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              🐾 Soundboard
            </button>
          </div>
        </div>

        {/* TAB 1: BUBBLE POP */}
        {activeTab === 'bubbles' && (
          <div className="mt-10 max-w-4xl mx-auto rounded-[36px] bg-gradient-to-b from-sky-50 via-white to-pink-50 border-4 border-kiki-sky/60 p-6 sm:p-8 shadow-xl">
            <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-kiki-yellow/30 rounded-2xl">
                  <StarIcon className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <h3 className="font-heading font-black text-xl text-slate-800">Sensory Bubble Pop</h3>
                  <p className="text-xs text-slate-500">Tap bubbles to pop them and discover friendly animals!</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="px-4 py-2 bg-white rounded-2xl border-2 border-kiki-coral/30 shadow-sm flex items-center gap-2">
                  <span className="text-xs font-heading font-bold text-slate-500">POPS:</span>
                  <span className="font-heading font-black text-xl text-kiki-coral">{popScore}</span>
                </div>
                <button
                  onClick={resetBubbles}
                  className="btn-bouncy px-4 py-2 bg-white hover:bg-slate-50 rounded-2xl border-2 border-slate-200 text-slate-700 font-heading text-xs font-bold"
                >
                  🔄 New Wave
                </button>
              </div>
            </div>

            {/* Bubble Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 py-8">
              {bubbles.map((b) => (
                <div key={b.id} className="flex justify-center items-center h-36">
                  {!b.popped ? (
                    <button
                      onClick={() => handlePop(b.id, b)}
                      className={`btn-bouncy w-28 h-28 sm:w-32 sm:h-32 rounded-full border-4 shadow-bouncy hover:scale-105 active:scale-90 transition-all flex flex-col items-center justify-center p-2 group ${b.color}`}
                    >
                      <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
                        <img src={b.img} alt={b.label} className="w-full h-full object-contain filter drop-shadow-sm group-hover:rotate-6 transition-transform" />
                      </div>
                      <span className="font-heading font-bold text-xs mt-1">{b.label}</span>
                    </button>
                  ) : (
                    <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full border-2 border-dashed border-slate-300 flex flex-col items-center justify-center bg-white/40 animate-pop">
                      <span className="text-2xl">✨</span>
                      <span className="text-[11px] font-heading font-bold text-slate-400 mt-1">Popped!</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pop announcement footer */}
            <div className="mt-2 p-4 bg-white/90 rounded-2xl border border-slate-200 text-center flex items-center justify-center gap-3">
              <span className="text-xl">💬</span>
              <p className="font-heading font-bold text-sm text-slate-700">
                {lastPopped ? (
                  <>You popped the <strong className="text-kiki-coral">{lastPopped.label}</strong>! It says: <span className="text-kiki-blue">"{lastPopped.sound}"</span></>
                ) : (
                  'Tap any floating bubble to hear its special sound!'
                )}
              </p>
            </div>
          </div>
        )}

        {/* TAB 2: MATH ISLAND */}
        {activeTab === 'math' && (
          <div className="mt-10 max-w-3xl mx-auto rounded-[36px] bg-gradient-to-b from-sky-50 via-white to-amber-50 border-4 border-kiki-blue/40 p-6 sm:p-8 shadow-xl">
            
            {/* Top Island Header */}
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-heading font-bold bg-kiki-blue text-white">
                  {mathQuestions[currentMathIdx].island}
                </span>
                <span className="text-xs text-slate-400 font-bold">
                  Question {currentMathIdx + 1} of {mathQuestions.length}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {[...Array(mathStars)].map((_, i) => (
                  <StarIcon key={i} className="w-5 h-5 text-kiki-yellow" />
                ))}
              </div>
            </div>

            {!showChestReward ? (
              <div className="py-6 space-y-6">
                
                {/* Miss Kiki Prompt */}
                <div className="flex items-start gap-4 p-4 rounded-3xl bg-white border-2 border-kiki-sky shadow-sm">
                  <img src="/assets/images/kiki-hero.png" alt="Miss Kiki" className="w-14 h-14 object-contain" />
                  <div>
                    <span className="text-xs font-heading font-bold text-kiki-blue uppercase tracking-wider">Miss Kiki asks:</span>
                    <h3 className="text-xl font-heading font-bold text-slate-800 mt-0.5">
                      {mathQuestions[currentMathIdx].prompt}
                    </h3>
                  </div>
                </div>

                {/* Visual Manipulatives (if applicable) */}
                {mathQuestions[currentMathIdx].count && (
                  <div className="flex flex-wrap items-center justify-center gap-3 py-4 bg-sky-50/60 rounded-3xl border border-sky-100">
                    {[...Array(mathQuestions[currentMathIdx].count)].map((_, i) => (
                      <div key={i} className="w-12 h-12 rounded-2xl bg-white border-2 border-amber-300 flex items-center justify-center text-2xl shadow-sm animate-bounce-slow" style={{ animationDelay: `${i * 0.15}s` }}>
                        {mathQuestions[currentMathIdx].icon || '⭐'}
                      </div>
                    ))}
                  </div>
                )}

                {/* Visual Planks for Addition */}
                {mathQuestions[currentMathIdx].count1 && (
                  <div className="flex items-center justify-center gap-4 py-4 bg-amber-50/60 rounded-3xl border border-amber-100 text-xl font-heading font-bold text-slate-700">
                    <div className="flex gap-1.5">
                      {[...Array(mathQuestions[currentMathIdx].count1)].map((_, i) => (
                        <div key={i} className="w-9 h-9 rounded-xl bg-amber-400 text-white flex items-center justify-center shadow-xs">
                          🪵
                        </div>
                      ))}
                    </div>
                    <span>+</span>
                    <div className="flex gap-1.5">
                      {[...Array(mathQuestions[currentMathIdx].count2)].map((_, i) => (
                        <div key={i} className="w-9 h-9 rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-xs">
                          🪵
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Multiple Choice Options */}
                <div className="grid grid-cols-3 gap-4">
                  {mathQuestions[currentMathIdx].options.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleMathChoice(option)}
                      className={`btn-bouncy py-4 px-6 rounded-2xl font-heading font-bold text-xl border-2 transition-all shadow-bouncy ${
                        selectedOption === option
                          ? option === mathQuestions[currentMathIdx].answer
                            ? 'bg-kiki-green text-white border-emerald-600'
                            : 'bg-rose-100 text-rose-800 border-rose-300'
                          : 'bg-white hover:bg-slate-50 text-slate-800 border-slate-200'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                {/* Feedback message */}
                {mathFeedback && (
                  <div className={`p-4 rounded-2xl text-center font-heading font-bold text-sm transition-all ${
                    mathFeedback.correct ? 'bg-emerald-100 text-emerald-800 border border-emerald-300 animate-pop' : 'bg-amber-100 text-amber-900 border border-amber-300'
                  }`}>
                    {mathFeedback.text}
                  </div>
                )}

              </div>
            ) : (
              /* Island Cleared Reward View */
              <div className="py-8 text-center space-y-4 animate-pop">
                <div className="text-6xl animate-bounce">🏆 🏝️</div>
                <h3 className="text-2xl font-heading font-black text-slate-900">
                  Island Completed! You Saved the Cove!
                </h3>
                <p className="text-slate-600 font-medium">
                  Miss Kiki is so proud of you! You earned {mathStars} stars and unlocked the Island Treasure Chest.
                </p>
                <button
                  onClick={resetMath}
                  className="btn-bouncy px-6 py-3 rounded-2xl bg-kiki-blue text-white font-heading font-bold text-base shadow-bouncy"
                >
                  Play Island Again 🔄
                </button>
              </div>
            )}

          </div>
        )}

        {/* TAB 3: ANIMAL & BUDDY SOUNDBOARD */}
        {activeTab === 'soundboard' && (
          <div className="mt-10 max-w-5xl mx-auto rounded-[36px] bg-slate-50/80 border-4 border-kiki-purple/30 p-6 sm:p-8 shadow-xl">
            <div className="flex items-center justify-between pb-6 border-b border-slate-200">
              <div>
                <h3 className="font-heading font-black text-xl text-slate-800">
                  Meet the Characters &amp; Animals
                </h3>
                <p className="text-xs text-slate-500">Tap anyone to hear their voice and friendly words!</p>
              </div>
              <span className="text-xs font-heading font-bold text-kiki-purple px-3 py-1 bg-kiki-lavender rounded-full">
                12 Characters
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 pt-6">
              {soundboardCharacters.map((char) => (
                <button
                  key={char.name}
                  onClick={() => handleSoundboardTap(char)}
                  className={`btn-bouncy p-4 rounded-3xl border-2 flex flex-col items-center justify-center text-center transition-all shadow-sm hover:shadow-md ${
                    activeSpeaker === char.name
                      ? 'border-kiki-purple bg-kiki-lavender scale-105'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  style={{ backgroundColor: activeSpeaker === char.name ? '#E8DEFF' : char.color }}
                >
                  <div className="w-16 h-16 flex items-center justify-center">
                    <img
                      src={char.art}
                      alt={char.name}
                      className={`w-full h-full object-contain filter drop-shadow-sm ${activeSpeaker === char.name ? 'animate-wiggle' : ''}`}
                    />
                  </div>
                  <span className="font-heading font-bold text-sm text-slate-800 mt-2">
                    {char.name}
                  </span>
                  <span className="text-[11px] font-bold text-kiki-purple mt-0.5">
                    {char.soundWord}
                  </span>
                  {activeSpeaker === char.name && (
                    <div className="absolute -top-2 -right-2 bg-kiki-coral text-white p-1 rounded-full text-xs animate-ping">
                      <HeartIcon className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

      </div>
    </section>
  );
}
